import {BOOKING_STATUS} from "../enum/booking-status.enum";
import {IApiBookingFinancial, IBookingCustomer, IBookingZone} from "../interfaces/booking.interface";
import {BookingSpace} from "./space.model";

export class Booking {
  readonly id: string;
  readonly customer: IBookingCustomer;
  readonly name: string;
  readonly spaces: readonly BookingSpace[];
  readonly zone: IBookingZone;
  readonly date: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly partySize: number;
  readonly notes: string;
  readonly bookerNotes: string;
  readonly referredBy: { id: string; name: string };
  readonly createdAt: number;
  readonly createdBy: string;
  readonly updatedBy: string;
  readonly lockedBy: string;
  public releasedBy: string;
  public releasedAt: string | number;
  private _accountId: string;
  private _status: BOOKING_STATUS;
  private _financial: IApiBookingFinancial;
  private _isActive: boolean;
  private _isClosed: boolean;

  updatedAt: number;

  constructor(
    id: string,
    status: BOOKING_STATUS,
    isActive: boolean,
    accountId: string,
    customer: IBookingCustomer,
    name: string,
    spaces: readonly BookingSpace[],
    zone: IBookingZone,
    date: string,
    startTime: string,
    endTime: string,
    partySize: number,
    financial: IApiBookingFinancial,
    lockedBy: string,
    notes: string,
    bookerNotes: string,
    referredBy: { id: string; name: string },
    createdAt: number,
    createdBy: string,
    updatedAt: number,
    updatedBy: string,
    isClosed: boolean
  ) {
    this.id = id;
    this._status = status;
    this._accountId = accountId;
    this.customer = customer;
    this.name = name;
    this.spaces = spaces;
    this.zone = zone;
    this.date = date;
    this.startTime = startTime;
    this.endTime = endTime;
    this.partySize = partySize;
    this._financial = financial;
    this.notes = notes;
    this.bookerNotes = bookerNotes;
    this.referredBy = referredBy;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt || createdAt;
    this.createdBy = createdBy;
    this.updatedBy = updatedBy;
    this.lockedBy = lockedBy;
    this.releasedBy = '';
    this.releasedAt = '';
    this._isActive = isActive;
    this._isClosed = isClosed;
  }

  get status(): BOOKING_STATUS {
    return this._status;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  get accountId(): string {
    return this._accountId;
  }

  get financial(): IApiBookingFinancial {
    return this._financial;
  }

  get currentConsumption(): number {
    return this._financial.currentConsumption ?? 0;
  }

  get hasCoverCharge(): boolean {
    return (this._financial.coverCharge?.total ?? 0) > 0;
  }

  get minConsumption(): number {
    const price = this._financial.bookingPrice ?? 0;
    const cover = this._financial.coverCharge?.total ?? 0;
    const paid = this._financial.paid ?? 0;

    return Math.max(0, price - cover, paid - cover);
  }

  get isClosed(): boolean {
    return this._isClosed;
  }

  get consumptionPercentage(): number {
    const min = this.minConsumption;
    const current = this.currentConsumption;

    return min === 0 ? 0 : Math.min(100, Math.round((current / min) * 100));
  }

  get tableNumber(): string {
    const [first, ...others] = this.spaces;

    if (!first) {
      return ''
    }

    const label = first.name ?? first.number;
    const suffix = others.length > 0 ? `+${others.length}` : '';

    return `${label}${suffix}`;
  }

  get canBeClosed(): boolean {
    return this._financial.total > 0
      && this._financial.paid >= this._financial.total
      && this._financial.toBePaid === 0;
  }

  get isLockedByOther(): boolean {
    return !!this.lockedBy;
  }

  updateStatus(newStatus: BOOKING_STATUS): void {
    this._status = newStatus;
    this.updatedAt = Date.now();
  }

  release(releasedBy: string, releasedAt: string | number): void {
    this._status = BOOKING_STATUS.released;
    this.releasedBy = releasedBy;
    this.releasedAt = releasedAt;
  }

  registerConsumption(entriesTotalAmount: number): void {
    this.recalculateFinancial(entriesTotalAmount);
  }

  private recalculateFinancial(entriesTotal: number): void {
    const paid = this._financial.paid ?? 0;
    const price = Math.max(paid, this._financial.bookingPrice ?? 0);
    const min = this.minConsumption;
    const excess = Math.max(0, entriesTotal - min);
    const toPay = Math.max(0, price + excess - paid);
    const pending = Math.max(0, min - entriesTotal);
    
    this._financial = {
      ...this._financial,
      currentConsumption: entriesTotal,
      pendingConsumption: pending,
      toBePaid: toPay,
    };
  }
}
